package com.yendo.mx.ui.profile

import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import coil3.request.crossfade
import com.yendo.mx.repository.AuthRepository
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.utils.BackHandler
import com.yendo.mx.utils.rememberYendoImagePickerLauncher
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import yendo.shared.generated.resources.*

enum class EditStep { MAIN, NOMBRE, TELEFONO, CORREO, PASSWORD }

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EditarPerfilScreen(onBack: () -> Unit, onCerrarSesion: () -> Unit) {
    var pasoActual by remember { mutableStateOf(EditStep.MAIN) }

    val isDark = isSystemInDarkTheme()
    val colorFondoGlobal = if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8)
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray
    val colorBordeOutlined = if (isDark) Color(0xFF3A3A3C) else Color(0xFFE0E0E0)
    val colorFondoTarjetas = if (isDark) Color(0xFF1E1E1E) else Color.White

    val usuario by AuthRepository.usuarioActual.collectAsState()
    val scope = rememberCoroutineScope()
    val focusManager = androidx.compose.ui.platform.LocalFocusManager.current
    val context = LocalPlatformContext.current

    val listaProveedores = remember(usuario) { AuthRepository.obtenerListaProveedores() }
    val esSocial = listaProveedores.any { it == "Google" || it == "Apple" }
    val esTelefonoPuro = listaProveedores.size == 1 && listaProveedores.first() == "Telefono"
    val esCorreoPuro = listaProveedores.any { it == "Correo" }

    val nombreOriginal = usuario?.nombre ?: ""
    val apellidoOriginal = usuario?.apellido ?: ""
    val telefonoOriginal = usuario?.telefono ?: ""
    val correoOriginal = usuario?.email ?: ""

    var nombre by remember { mutableStateOf("") }
    var apellido by remember { mutableStateOf("") }
    var codigoPais by remember { mutableStateOf("+52") }
    var telefono by remember { mutableStateOf("") }
    var correo by remember { mutableStateOf("") }
    var confirmarCorreo by remember { mutableStateOf("") } 

    LaunchedEffect(usuario) {
        if (usuario != null) {
            if (nombre.isEmpty()) nombre = usuario?.nombre ?: ""
            if (apellido.isEmpty()) apellido = usuario?.apellido ?: ""
            if (telefono.isEmpty()) {
                val tel = usuario?.telefono ?: ""
                val pre = if (tel.startsWith("+1")) "+1" else "+52"
                codigoPais = pre
                telefono = if (tel.startsWith("+")) tel.removePrefix(pre) else tel
            }
            if (correo.isEmpty()) correo = usuario?.email ?: ""
        }
    }

    val fotoUrl = usuario?.fotoPerfil ?: ""
    var passActual by remember { mutableStateOf("") }
    var passNueva by remember { mutableStateOf("") }
    var passConfirmar by remember { mutableStateOf("") }

    var menuPaisesExpanded by remember { mutableStateOf(false) }
    val listaPaises = listOf("+52" to "MX", "+1" to "US/CA")

    var guardando by remember { mutableStateOf(false) }
    var codigoSms by remember { mutableStateOf("") }
    var verificationIdSms by remember { mutableStateOf("") } 
    var mensajeError by remember { mutableStateOf("") }
    var mostrarAlertaCorreo by remember { mutableStateOf(false) }
    var mostrarAlertaSesionCerrada by remember { mutableStateOf(false) }
    var mensajeCierreSesion by remember { mutableStateOf("") } 
    var mostrarEliminarCuenta by remember { mutableStateOf(false) } 

    val photoPickerLauncher = rememberYendoImagePickerLauncher(
        onResult = { images ->
            val firstImage = images.firstOrNull()
            if (firstImage != null) {
                guardando = true; mensajeError = ""
                scope.launch {
                    val res = AuthRepository.subirFotoPerfilKMP(firstImage)
                    guardando = false
                    if (res != "OK") mensajeError = res
                }
            }
        }
    )

    val iniciales = remember(nombre, apellido) {
        val n = nombre.firstOrNull()?.uppercase() ?: ""
        val a = apellido.firstOrNull()?.uppercase() ?: ""
        "$n$a"
    }

    BackHandler {
        if (pasoActual != EditStep.MAIN && !guardando) pasoActual = EditStep.MAIN
        else if (!guardando) onBack()
    }

    if (mostrarEliminarCuenta) {
        EliminarCuentaScreen(onBack = { mostrarEliminarCuenta = false }, onCuentaEliminada = { mostrarEliminarCuenta = false; onCerrarSesion() })
        return
    }

    Scaffold(
        modifier = Modifier.fillMaxSize().clickable(interactionSource = remember { androidx.compose.foundation.interaction.MutableInteractionSource() }, indication = null) { focusManager.clearFocus() },
        topBar = {
            TopAppBar(
                title = { Text(when(pasoActual){ EditStep.MAIN -> "Editar Perfil"; EditStep.NOMBRE -> "Nombre completo"; EditStep.TELEFONO -> "Teléfono"; EditStep.CORREO -> "Correo electrónico"; EditStep.PASSWORD -> "Contraseña" }, fontWeight = FontWeight.ExtraBold, color = colorTextoPrincipal) },
                navigationIcon = { IconButton(onClick = { if (pasoActual != EditStep.MAIN) pasoActual = EditStep.MAIN else onBack() }, enabled = !guardando) { Icon(Icons.AutoMirrored.Filled.ArrowBack, null, tint = colorTextoPrincipal) } },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = colorFondoGlobal)
            )
        },
        containerColor = colorFondoGlobal
    ) { padding ->
        AnimatedContent(targetState = pasoActual, transitionSpec = { slideInHorizontally { it } + fadeIn() togetherWith slideOutHorizontally { -it } + fadeOut() }, label = "PerfilPasos", modifier = Modifier.fillMaxSize().padding(padding)) { paso ->
            Column(modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(20.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                if (mensajeError.isNotEmpty()) Card(colors = CardDefaults.cardColors(containerColor = Color(0xFFFFEBEE)), modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp)) { Text(mensajeError, color = Color.Red, fontSize = 13.sp, modifier = Modifier.padding(12.dp), textAlign = TextAlign.Center) }
                when (paso) {
                    EditStep.MAIN -> {
                        Box(
                            modifier = Modifier
                                .size(110.dp)
                                .clip(CircleShape)
                                .background(if (fotoUrl.isEmpty()) RosaYendo.copy(alpha = 0.1f) else Color.Transparent)
                                .clickable { photoPickerLauncher.launch() },
                            contentAlignment = Alignment.Center
                        ) {
                            if (fotoUrl.isNotEmpty()) {
                                AsyncImage(
                                    model = ImageRequest.Builder(LocalPlatformContext.current).data(fotoUrl).crossfade(true).build(),
                                    contentDescription = null,
                                    modifier = Modifier.fillMaxSize(),
                                    contentScale = ContentScale.Crop
                                )
                            } else {
                                Text(
                                    text = iniciales.ifEmpty { "Y" },
                                    color = RosaYendo,
                                    fontWeight = FontWeight.Black,
                                    fontSize = 40.sp
                                )
                            }
                            // Icono de cámara flotante
                            Box(
                                modifier = Modifier
                                    .align(Alignment.BottomEnd)
                                    .size(32.dp)
                                    .clip(CircleShape)
                                    .background(RosaYendo)
                                    .padding(6.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                Icon(Icons.Default.PhotoCamera, null, tint = Color.White, modifier = Modifier.fillMaxSize())
                            }
                        }

                        Spacer(Modifier.height(32.dp))

                        CategoryLabel("Información Personal", colorTextoSecundario)
                        
                        FilaEdicionClean(
                            icono = Icons.Default.Person,
                            fondoIcono = Color(0xFFFF9800),
                            titulo = "Nombre",
                            valor = "$nombre $apellido",
                            colorTexto = colorTextoPrincipal,
                            colorSubtexto = colorTextoSecundario,
                            onClick = { mensajeError = ""; pasoActual = EditStep.NOMBRE }
                        )

                        FilaEdicionClean(
                            icono = Icons.Default.Phone,
                            fondoIcono = Color(0xFF4CAF50),
                            titulo = "Teléfono",
                            valor = telefonoOriginal.ifEmpty { "Agregar teléfono" },
                            colorTexto = colorTextoPrincipal,
                            colorSubtexto = colorTextoSecundario,
                            onClick = { mensajeError = ""; pasoActual = EditStep.TELEFONO }
                        )

                        Spacer(Modifier.height(16.dp))

                        CategoryLabel("Seguridad de la Cuenta", colorTextoSecundario)
                        
                        val permiteEditarCorreo = esTelefonoPuro || esCorreoPuro || !esSocial
                        FilaEdicionClean(
                            icono = Icons.Default.Email,
                            fondoIcono = Color(0xFF2196F3),
                            titulo = "Correo",
                            valor = if (permiteEditarCorreo) correoOriginal.ifEmpty { "Agregar correo" } 
                                    else "$correoOriginal${if (correoOriginal.contains("privaterelay")) " (Oculto)" else ""}",
                            colorTexto = colorTextoPrincipal,
                            colorSubtexto = colorTextoSecundario,
                            onClick = { 
                                if (permiteEditarCorreo) { mensajeError = ""; confirmarCorreo = ""; pasoActual = EditStep.CORREO }
                                else { mensajeError = "Tu correo está vinculado socialmente." }
                            }
                        )

                        listaProveedores.forEach { prov ->
                            when(prov) {
                                "Google" -> {
                                    FilaEdicionCleanConLogo(
                                        painter = painterResource(Res.drawable.google_g_logo),
                                        fondoIcono = Color.White,
                                        titulo = "Método de acceso",
                                        valor = "Google",
                                        colorTexto = colorTextoPrincipal,
                                        colorSubtexto = colorTextoSecundario,
                                        onClick = { mensajeError = "Vinculado con Google" }
                                    )
                                }
                                "Apple" -> {
                                    FilaEdicionClean(
                                        icono = Icons.Default.PhoneIphone,
                                        fondoIcono = Color.Black,
                                        titulo = "Método de acceso",
                                        valor = "Apple",
                                        colorTexto = colorTextoPrincipal,
                                        colorSubtexto = colorTextoSecundario,
                                        onClick = { mensajeError = "Vinculado con Apple" }
                                    )
                                }
                                "Telefono" -> {
                                    FilaEdicionClean(
                                        icono = Icons.Default.PhoneAndroid,
                                        fondoIcono = RosaYendo,
                                        titulo = "Método de acceso",
                                        valor = "Teléfono",
                                        colorTexto = colorTextoPrincipal,
                                        colorSubtexto = colorTextoSecundario,
                                        onClick = { mensajeError = "Protegido por Teléfono" }
                                    )
                                }
                                "Correo" -> {
                                    FilaEdicionClean(
                                        icono = Icons.Default.Lock,
                                        fondoIcono = RosaYendo,
                                        titulo = "Método de acceso",
                                        valor = "Contraseña",
                                        colorTexto = colorTextoPrincipal,
                                        colorSubtexto = colorTextoSecundario,
                                        onClick = { mensajeError = ""; pasoActual = EditStep.PASSWORD }
                                    )
                                }
                            }
                        }

                        Spacer(Modifier.height(32.dp))

                        CategoryLabel("Zona de Peligro", Color.Red)
                        
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(16.dp))
                                .background(Color.Red.copy(alpha = 0.05f))
                                .clickable { mostrarEliminarCuenta = true }
                                .padding(16.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Box(
                                modifier = Modifier
                                    .size(40.dp)
                                    .clip(CircleShape)
                                    .background(Color.Red),
                                contentAlignment = Alignment.Center
                            ) {
                                Icon(Icons.Default.DeleteForever, null, tint = Color.White, modifier = Modifier.size(20.dp))
                            }
                            Spacer(Modifier.width(16.dp))
                            Column {
                                Text("Eliminar mi cuenta", color = Color.Red, fontWeight = FontWeight.Bold, fontSize = 15.sp)
                                Text("Esta acción es irreversible.", color = Color.Red.copy(alpha = 0.6f), fontSize = 12.sp)
                            }
                        }
                    }
                    EditStep.NOMBRE -> {
                        OutlinedTextField(value = nombre, onValueChange = { nombre = it }, label = { Text("Nombre(s)") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = RosaYendo, unfocusedBorderColor = colorBordeOutlined, focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal))
                        Spacer(Modifier.height(16.dp))
                        OutlinedTextField(value = apellido, onValueChange = { apellido = it }, label = { Text("Apellidos") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = RosaYendo, unfocusedBorderColor = colorBordeOutlined, focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal))
                        Spacer(Modifier.height(32.dp))
                        BotonGuardarPerfil(habilitado = nombre.trim().length >= 2 && apellido.trim().length >= 2 && (nombre != nombreOriginal || apellido != apellidoOriginal), cargando = guardando) { scope.launch { guardando = true; mensajeError = ""; val res = AuthRepository.actualizarPerfilBase(nombre.trim(), apellido.trim()); guardando = false; if (res == "OK") pasoActual = EditStep.MAIN else mensajeError = res } }
                    }
                    EditStep.TELEFONO -> {
                        val inst = if (verificationIdSms.isEmpty()) "Al cambiar tu número, este se convertirá en tu nueva forma de acceso principal."
                                   else "Ingresa los 6 dígitos que enviamos al +$codigoPais $telefono"
                        
                        Text(inst, color = colorTextoSecundario, fontSize = 13.sp, modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp))
                        
                        OutlinedTextField(
                            value = telefono, 
                            onValueChange = { val c = it.filter { char -> char.isDigit() }; if (c.length <= 10) telefono = c }, 
                            label = { Text("Nuevo teléfono celular") }, 
                            enabled = verificationIdSms.isEmpty(),
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone), 
                            modifier = Modifier.fillMaxWidth(), 
                            shape = RoundedCornerShape(12.dp), 
                            colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = RosaYendo, unfocusedBorderColor = colorBordeOutlined, focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal), 
                            leadingIcon = { Box { Row(modifier = Modifier.clickable(enabled = verificationIdSms.isEmpty()) { menuPaisesExpanded = true }.padding(horizontal = 8.dp), verticalAlignment = Alignment.CenterVertically) { Text(codigoPais, fontWeight = FontWeight.Bold, color = if (verificationIdSms.isEmpty()) RosaYendo else Color.Gray); Icon(Icons.Default.ArrowDropDown, null, tint = if (verificationIdSms.isEmpty()) RosaYendo else Color.Gray) } ; DropdownMenu(expanded = menuPaisesExpanded, onDismissRequest = { menuPaisesExpanded = false }) { listaPaises.forEach { (c, p) -> DropdownMenuItem(text = { Text("$c ($p)") }, onClick = { codigoPais = c; menuPaisesExpanded = false }) } } } },
                            trailingIcon = {
                                if (verificationIdSms.isNotEmpty()) {
                                    IconButton(onClick = { verificationIdSms = ""; codigoSms = ""; mensajeError = "" }) {
                                        Icon(Icons.Default.Edit, "Cambiar número", tint = RosaYendo, modifier = Modifier.size(20.dp))
                                    }
                                }
                            }
                        )

                        AnimatedVisibility(visible = verificationIdSms.isNotEmpty(), enter = expandVertically() + fadeIn(), exit = shrinkVertically() + fadeOut()) {
                            Column {
                                Spacer(Modifier.height(16.dp))
                                OutlinedTextField(value = codigoSms, onValueChange = { if (it.length <= 6) codigoSms = it }, label = { Text("Código de verificación") }, placeholder = { Text("000000") }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number), modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = RosaYendo, unfocusedBorderColor = colorBordeOutlined, focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal))
                            }
                        }

                        Spacer(Modifier.height(32.dp))
                        // 🚀 VALIDACIÓN DE CAMBIO REAL: Comparamos el número completo (con prefijo)
                        val numLimpioNuevo = telefono.filter { it.isDigit() }
                        val telActualCompleto = "$codigoPais$numLimpioNuevo"
                        val esMismo = telActualCompleto == telefonoOriginal
                        
                        val habilitado = if (verificationIdSms.isEmpty()) {
                            numLimpioNuevo.length == 10 && !esMismo
                        } else {
                            codigoSms.length == 6
                        }

                        Button(
                            onClick = {
                                if (verificationIdSms.isEmpty()) {
                                    val telFinal = "$codigoPais$numLimpioNuevo"
                                    guardando = true; mensajeError = ""
                                    AuthRepository.enviarSmsValidacion(telFinal, context, { verId -> guardando = false; verificationIdSms = verId }, { err -> guardando = false; mensajeError = err }) 
                                } else {
                                    guardando = true
                                    val telFinal = "$codigoPais$numLimpioNuevo"
                                    AuthRepository.confirmarSmsYActualizar(verificationIdSms, codigoSms, nombre.trim(), apellido.trim(), telFinal, correo.trim(), { guardando = false; verificationIdSms = ""; codigoSms = ""; if (correo != correoOriginal) mostrarAlertaCorreo = true }, { e -> guardando = false; mensajeError = e })
                                }
                            },
                            modifier = Modifier.fillMaxWidth().height(56.dp), shape = RoundedCornerShape(12.dp), colors = ButtonDefaults.buttonColors(containerColor = RosaYendo), enabled = habilitado && !guardando
                        ) {
                            if (guardando) CircularProgressIndicator(color = Color.White, modifier = Modifier.size(24.dp), strokeWidth = 2.dp)
                            else Text(if (verificationIdSms.isEmpty()) "Enviar código SMS" else "Verificar y guardar cambios", fontWeight = FontWeight.Bold, color = Color.White, fontSize = 16.sp)
                        }
                    }
                    EditStep.CORREO -> {
                        OutlinedTextField(value = correo, onValueChange = { correo = it }, label = { Text("Nuevo correo electrónico") }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email), modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = RosaYendo, unfocusedBorderColor = colorBordeOutlined, focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal))
                        Spacer(Modifier.height(16.dp))
                        OutlinedTextField(value = confirmarCorreo, onValueChange = { confirmarCorreo = it }, label = { Text("Confirmar nuevo correo") }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email), modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), isError = confirmarCorreo.isNotEmpty() && confirmarCorreo != correo, colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = RosaYendo, unfocusedBorderColor = colorBordeOutlined, focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal))
                        if (esCorreoPuro) { Spacer(Modifier.height(16.dp)); OutlinedTextField(value = passActual, onValueChange = { passActual = it }, label = { Text("Contraseña actual") }, visualTransformation = androidx.compose.ui.text.input.PasswordVisualTransformation(), keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password), modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = RosaYendo, unfocusedBorderColor = colorBordeOutlined, focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal)) }
                        Spacer(Modifier.height(32.dp))
                        val isFormValid = correo.contains("@") && correo != correoOriginal && correo == confirmarCorreo && (!esCorreoPuro || passActual.isNotEmpty())
                        BotonGuardarPerfil(habilitado = isFormValid, cargando = guardando) { scope.launch { guardando = true; mensajeError = ""; if (esCorreoPuro) { val res = AuthRepository.cambiarCorreo(correo.trim(), passActual); guardando = false; if (res == "REVISA_TU_CORREO") { mensajeCierreSesion = "Enlace enviado a $correo. Inicia sesión con tus nuevos datos."; mostrarAlertaSesionCerrada = true } else mensajeError = res } else { val res = AuthRepository.actualizarPerfilBase(nombre, apellido, correo.trim(), telefonoOriginal); guardando = false; if (res == "OK") { mensajeCierreSesion = "Correo actualizado. Verifica tu bandeja."; mostrarAlertaSesionCerrada = true } else mensajeError = res } } }
                    }
                    EditStep.PASSWORD -> {
                        OutlinedTextField(value = passActual, onValueChange = { passActual = it }, label = { Text("Contraseña actual") }, visualTransformation = androidx.compose.ui.text.input.PasswordVisualTransformation(), keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password), modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = RosaYendo, unfocusedBorderColor = colorBordeOutlined, focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal))
                        Spacer(Modifier.height(16.dp))
                        OutlinedTextField(value = passNueva, onValueChange = { passNueva = it }, label = { Text("Nueva contraseña") }, visualTransformation = androidx.compose.ui.text.input.PasswordVisualTransformation(), keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password), modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = RosaYendo, unfocusedBorderColor = colorBordeOutlined, focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal))
                        Spacer(Modifier.height(16.dp))
                        OutlinedTextField(value = passConfirmar, onValueChange = { passConfirmar = it }, label = { Text("Confirmar contraseña") }, visualTransformation = androidx.compose.ui.text.input.PasswordVisualTransformation(), keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password), modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), isError = passConfirmar.isNotEmpty() && passConfirmar != passNueva, colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = RosaYendo, unfocusedBorderColor = colorBordeOutlined, focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal))
                        Spacer(Modifier.height(32.dp))
                        BotonGuardarPerfil(habilitado = passActual.isNotEmpty() && passNueva.length >= 6 && passNueva == passConfirmar, cargando = guardando) { scope.launch { guardando = true; mensajeError = ""; val res = AuthRepository.cambiarPassword(passActual, passNueva); guardando = false; if (res == "OK") { mensajeCierreSesion = "Credenciales actualizadas. Reinicia sesión."; mostrarAlertaSesionCerrada = true } else mensajeError = res } }
                    }
                }
            }
        }

        if (mostrarAlertaSesionCerrada) { AlertDialog(containerColor = colorFondoTarjetas, onDismissRequest = { }, title = { Text("Atención", color = colorTextoPrincipal, fontWeight = FontWeight.Bold) }, text = { Text(mensajeCierreSesion, color = colorTextoSecundario) }, confirmButton = { Button(onClick = { mostrarAlertaSesionCerrada = false; scope.launch { AuthRepository.cerrarSesion(); onCerrarSesion() } }, colors = ButtonDefaults.buttonColors(RosaYendo)) { Text("Entendido", color = Color.White) } } ) }
        if (mostrarAlertaCorreo) { AlertDialog(containerColor = colorFondoTarjetas, onDismissRequest = { mostrarAlertaCorreo = false; onBack() }, title = { Text("Revisa tu correo", color = colorTextoPrincipal, fontWeight = FontWeight.Bold) }, text = { Text("Perfil actualizado. Verifica tu bandeja.", color = colorTextoSecundario) }, confirmButton = { Button(onClick = { mostrarAlertaCorreo = false; onBack() }, colors = ButtonDefaults.buttonColors(RosaYendo)) { Text("Entendido", color = Color.White) } } ) }
    }
}

// --- COMPONENTES CLEAN ---

@Composable
fun FilaEdicionClean(
    icono: ImageVector,
    fondoIcono: Color,
    titulo: String,
    valor: String,
    colorTexto: Color,
    colorSubtexto: Color,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onClick() }
            .padding(vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(40.dp)
                .clip(CircleShape)
                .background(fondoIcono),
            contentAlignment = Alignment.Center
        ) {
            Icon(icono, null, tint = Color.White, modifier = Modifier.size(20.dp))
        }
        Spacer(Modifier.width(16.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(titulo, color = colorSubtexto, fontSize = 12.sp, fontWeight = FontWeight.Bold)
            Text(valor, color = colorTexto, fontSize = 16.sp, fontWeight = FontWeight.SemiBold)
        }
        Icon(Icons.Default.ChevronRight, null, tint = Color.LightGray.copy(alpha = 0.6f), modifier = Modifier.size(20.dp))
    }
}

@Composable
fun FilaEdicionCleanConLogo(
    painter: androidx.compose.ui.graphics.painter.Painter,
    fondoIcono: Color,
    titulo: String,
    valor: String,
    colorTexto: Color,
    colorSubtexto: Color,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onClick() }
            .padding(vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(40.dp)
                .clip(CircleShape)
                .background(fondoIcono),
            contentAlignment = Alignment.Center
        ) {
            androidx.compose.foundation.Image(painter = painter, contentDescription = null, modifier = Modifier.size(24.dp))
        }
        Spacer(Modifier.width(16.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(titulo, color = colorSubtexto, fontSize = 12.sp, fontWeight = FontWeight.Bold)
            Text(valor, color = colorTexto, fontSize = 16.sp, fontWeight = FontWeight.SemiBold)
        }
        Icon(Icons.Default.ChevronRight, null, tint = Color.LightGray.copy(alpha = 0.6f), modifier = Modifier.size(20.dp))
    }
}

@Composable
fun BotonGuardarPerfil(habilitado: Boolean, cargando: Boolean, onClick: () -> Unit) {
    Button(onClick = onClick, modifier = Modifier.fillMaxWidth().height(52.dp), colors = ButtonDefaults.buttonColors(containerColor = RosaYendo), shape = RoundedCornerShape(12.dp), enabled = habilitado && !cargando) {
        if (cargando) CircularProgressIndicator(color = Color.White, modifier = Modifier.size(24.dp), strokeWidth = 2.dp)
        else Text("Guardar cambios", color = Color.White, fontWeight = FontWeight.Bold, fontSize = 16.sp)
    }
}
